import {
	type FieldOption,
	Fieldset,
	FormControlLabel,
	FormHelperText,
	Legend,
	RadioButton,
	Stack,
	useText,
} from "@sveabank/cc-common";
import type { FieldState } from "final-form";
import { useEffect, useState } from "react";
import { Field, useForm } from "react-final-form";
import type { ClcBeneficialOwnerFormValue } from "../../../../types/clc-application";

type Props = {
	name: string;
};

export const BeneficialOwnershipRangeField = ({ name }: Props) => {
	const [t, texts] = useText();
	const { change, getFieldState, getState } = useForm();
	const formState = getState();
	const fieldState = getFieldState(name) as FieldState<ClcBeneficialOwnerFormValue> | undefined; // final form types are poor

	const [selectedOption, setSelectedOption] = useState<string | undefined>(undefined);

	const ownershipOptions: Array<FieldOption> = [
		{
			value: "100-100",
			label: "100%",
		},
		{
			value: "76-100",
			label: t(texts.newCompany.beneficialOwnership.controlRangeDescription, "75", "100"),
		},
		{
			value: "51-75",
			label: t(texts.newCompany.beneficialOwnership.controlRangeDescription, "50", "75"),
		},
		{
			value: "25-50",
			label: t(texts.newCompany.beneficialOwnership.controlRangeDescription, "25", "50"),
		},
		{
			value: "0-24",
			label: t(texts.newCompany.beneficialOwnership.controlRangeDescription, "0", "25"),
		},
	];

	// unfortunately need to use an effect because field state is undefined on first render:
	useEffect(() => {
		setSelectedOption(fieldState?.value?.ownershipPercent);
	}, [fieldState?.value?.ownershipPercent]);

	const validateField = (value: number) =>
		value === null || value === undefined ? t(texts.validation.mandatory) : undefined;

	const fieldName = `${name}.ownershipPercent`;

	return (
		<Fieldset>
			<Legend>{t(texts.newCompany.beneficialOwnership.controlRangeHeading)}</Legend>
			<Stack gap={2}>
				<Stack gap={4}>
					{ownershipOptions.map((o) => {
						return (
							<FormControlLabel label={o.label} key={o.value}>
								<RadioButton
									name="ownershipRange"
									value={o.value}
									checked={selectedOption === o.value}
									onChange={() => {
										setSelectedOption(o.value);
										change(`${name}.ownershipPercent`, o.value);
									}}
								/>
							</FormControlLabel>
						);
					})}
				</Stack>

				{/* invisible field only used for displaying validation: */}
				<Field<number> validate={validateField} name={fieldName}>
					{({ meta }) => {
						return meta.error && formState.submitFailed ? (
							<FormHelperText error>{meta.error}</FormHelperText>
						) : null;
					}}
				</Field>
			</Stack>
		</Fieldset>
	);
};
